// Generated by GitHub Copilot
// Leadership Search Component
//
// This component provides dedicated leadership responder search functionality
// for the Configuration page. It allows users to search across delivery streams
// to find leadership responders by name.

import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  FaSearch,
  FaTimes,
  FaExternalLinkAlt,
  FaInfoCircle,
} from "react-icons/fa";
import RefreshIndicator from '../Modern/Incidents/components/RefreshIndicator';
import usePolling from '../../hooks/usePolling';
import { POLLING_INTERVALS } from '../../config/polling';
import { validateSearchTerm } from '../../utils/validation';
import { trackLeadershipSearch, trackLeadershipSearchPagerDutyClick } from '../../utils/modernUITracking';
import styles from "./LeadershipSearch.module.css";

function LeadershipSearch({ user }) {
  // Generated by GitHub Copilot
  // Leadership responder search state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchCount, setSearchCount] = useState(0);

  // Generated by GitHub Copilot
  // Background refresh state
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Generated by GitHub Copilot
  // Debounced search term for automatic search
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Generated by GitHub Copilot
  // Check if user has configuration privilege for read-write access
  const hasConfigurationPrivilege =
    user && user.privileges && user.privileges.configuration;

  // Generated by GitHub Copilot
  // Check if user has any access to view delivery streams
  const canViewDeliveryStreams =
    user &&
    user.privileges &&
    (user.privileges.configuration ||
      user.privileges.editor ||
      user.privileges.deity);

  // Generated by GitHub Copilot
  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1500); // 1500ms debounce delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Generated by GitHub Copilot
  // Perform leadership responder search using the API endpoint
  const performLeadershipSearch = useCallback(
    async (searchTerm, isBackgroundRefresh = false) => {
      if (!canViewDeliveryStreams || !searchTerm.trim()) {
        return;
      }

      if (isBackgroundRefresh) {
        setRefreshing(true);
      } else {
        setSearchLoading(true);
        setSearchError("");
      }

      try {
        // VALIDATE search term before API call
        const validatedTerm = validateSearchTerm(searchTerm, 100);

        const response = await fetch(
          `/api/v1/search/leadership-responders?search=${encodeURIComponent(
            validatedTerm
          )}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Search failed: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();

        // Set search results with enhanced context - ensure we have valid data
        setSearchResults(data.results || []);
        setSearchCount(data.count || 0);
        setIsSearchActive(true);
        setLastUpdated(new Date());
        setIsInitialLoad(false);

        // Track successful search
        if (!isBackgroundRefresh) {
          trackLeadershipSearch(searchTerm, data.count || 0, {
            success: true
          });
        }
      } catch (err) {
        console.error("Leadership search failed:", err);

        // Handle validation errors
        if (err.message?.includes('Search term')) {
          setSearchError(err.message);
          if (isBackgroundRefresh) {
            setRefreshing(false);
          } else {
            setSearchLoading(false);
          }
          return;
        }

        // During background refresh, log but keep old data
        if (isBackgroundRefresh) {
          console.warn('Background refresh failed, keeping existing data:', err);
        } else {
          // Provide more specific error messages based on the error
          let errorMessage =
            "Failed to search leadership responders. Please try again.";

          if (err.message && err.message.includes("PagerDuty")) {
            errorMessage =
              "PagerDuty service is currently unavailable. Please try again later or contact support if the issue persists.";
          } else if (err.message && err.message.includes("500")) {
            errorMessage =
              "Server error occurred while searching. Please try again later.";
          } else if (err.message && err.message.includes("network")) {
            errorMessage =
              "Network error occurred. Please check your connection and try again.";
          }

          setSearchError(errorMessage);

          // Track failed search
          if (!isBackgroundRefresh) {
            trackLeadershipSearch(searchTerm, 0, {
              success: false,
              error: errorMessage
            });
          }
        }
      } finally {
        if (isBackgroundRefresh) {
          setRefreshing(false);
        } else {
          setSearchLoading(false);
        }
      }
    },
    [canViewDeliveryStreams]
  );

  // Generated by GitHub Copilot
  // Automatic search when debounced term changes
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      performLeadershipSearch(debouncedSearchTerm);
    } else if (isSearchActive) {
      // Clear search results when search term is empty
      setSearchResults([]);
      setSearchCount(0);
      setIsSearchActive(false);
    }
  }, [debouncedSearchTerm, performLeadershipSearch, isSearchActive]);

  // Generated by GitHub Copilot
  // Handle search input change
  const handleSearchInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchTerm(value);

      // Clear search error when user starts typing
      if (searchError) {
        setSearchError("");
      }
    },
    [searchError]
  );

  // Generated by GitHub Copilot
  // Handle manual search (Enter key or button click)
  const handleSearch = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
      }

      if (searchTerm.trim()) {
        performLeadershipSearch(searchTerm.trim());
      }
    },
    [searchTerm, performLeadershipSearch]
  );

  // Generated by GitHub Copilot
  // Clear search and reset state
  const clearSearch = useCallback(() => {
    setSearchTerm("");
    setSearchError("");
    setSearchResults([]);
    setSearchCount(0);
    setIsSearchActive(false);
  }, []);

  // Generated by GitHub Copilot
  // Background auto-refresh
  usePolling(
    () => performLeadershipSearch(debouncedSearchTerm, true),
    POLLING_INTERVALS.LEADERSHIP_SEARCH,
    {
      enabled: !isInitialLoad && isSearchActive && !searchLoading && debouncedSearchTerm.trim().length > 0,
      pauseWhenHidden: true
    }
  );

  // Generated by GitHub Copilot
  // If user doesn't have permission, show access denied
  if (!canViewDeliveryStreams) {
    return (
      <div className={styles.alert}>
        <div className={styles.alertHeader}>
          <FaInfoCircle className={styles.alertIcon} />
          <h4 className={styles.alertHeading}>Access Restricted</h4>
        </div>
        <p className={styles.alertText}>
          You need configuration, editor, or administrator privileges to access
          leadership search functionality.
        </p>
        <hr className={styles.alertDivider} />
        <p className={styles.alertFooter}>
          Contact your administrator if you need access to this feature.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header and Description */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.titleRow}>
            <h6 className={styles.title}>Leadership Responder Search</h6>
            <div className={styles.badges}>
              {!hasConfigurationPrivilege && (
                <span className={styles.badge}>Read Only</span>
              )}
              {isSearchActive && (
                <span className={`${styles.badge} ${styles.badgePrimary}`}>
                  {searchCount} result{searchCount !== 1 ? "s" : ""}
                </span>
              )}
              <RefreshIndicator
                isRefreshing={refreshing}
                lastUpdated={lastUpdated}
                showTimestamp={!refreshing && !!lastUpdated}
              />
            </div>
          </div>
          <p className={styles.description}>
            Search across delivery streams to find leadership responders by
            name. Searches through both IC and Leadership workflow
            configurations.
          </p>
        </div>

        {/* Info Card */}
        <div className={styles.infoCard}>
          <FaInfoCircle className={styles.infoIcon} />
          <div className={styles.infoContent}>
            <strong>How it works:</strong> Enter a name to search across all
            delivery stream workflows for matching leadership responders.
            Results show which delivery streams and workflows contain the
            person you're searching for.
          </div>
        </div>
      </div>

      {/* Search Interface */}
      <form onSubmit={handleSearch} className={styles.searchForm}>
        <div className={`${styles.searchInputGroup}${searchLoading ? ` ${styles.searchLoading}` : ""}`}>
          <input
            type="text"
            placeholder={`Search for leadership responders by name${
              isSearchActive ? ` (${searchCount} found)` : ""
            }...`}
            value={searchTerm}
            onChange={handleSearchInputChange}
            disabled={searchLoading}
            className={styles.searchInput}
            aria-label="Search for leadership responders"
          />
          <button
            type="button"
            className={`${styles.searchButton} ${isSearchActive ? styles.searchButtonActive : ""}`}
            onClick={handleSearch}
            disabled={searchLoading}
            title="Search leadership responders"
            aria-label="Search"
          >
            {searchLoading ? (
              <div className={styles.spinner} aria-label="Loading..." />
            ) : (
              <FaSearch />
            )}
          </button>
          {(searchTerm || isSearchActive) && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={clearSearch}
              disabled={searchLoading}
              title="Clear search"
              aria-label="Clear search"
            >
              <FaTimes />
            </button>
          )}
        </div>
      </form>

      {/* Search Feedback */}
      {isSearchActive && !searchLoading && !searchError && (
        <div className="mb-3">
          <small className="text-muted">
            {searchCount > 0
              ? `Found ${searchCount} delivery stream${
                  searchCount !== 1 ? "s" : ""
                } with leadership responder "${searchTerm}"`
              : `No delivery streams have leadership responder "${searchTerm}". Try a different name or check spelling.`}
          </small>
        </div>
      )}

      {/* Error Display */}
      {searchError && (
        <div className={styles.alert} style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <small>{searchError}</small>
            <button
              onClick={() => setSearchError("")}
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                padding: 0,
                fontSize: '1rem'
              }}
              aria-label="Dismiss error"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      )}

      {/* Results Table - Desktop */}
      <table className={styles.resultsTable}>
        <thead>
          <tr>
            <th>Delivery Stream</th>
            <th>Portfolio</th>
            <th>Matched Workflows</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {searchResults && searchResults.length > 0 ? (
            searchResults.map((result) => (
              <tr key={result.delivery_stream.id}>
                <td>
                  {canViewDeliveryStreams ? (
                    <Link
                      to={`/configuration/deliverystream/${result.delivery_stream.id}`}
                      title={
                        hasConfigurationPrivilege
                          ? "Edit delivery stream"
                          : "View delivery stream (read-only)"
                      }
                    >
                      {result.delivery_stream.name}
                    </Link>
                  ) : (
                    <span className={styles.readOnlyText}>
                      {result.delivery_stream.name}
                    </span>
                  )}
                </td>
                <td>{result.delivery_stream.portfolio}</td>
                <td>
                  <div className={styles.workflowMatches}>
                    {result.matched_workflows
                      .slice(0, 2)
                      .map((workflow, index) => (
                        <div key={workflow.workflow_id}>
                          <span
                            className={`${styles.badge} ${
                              workflow.workflow_type === "IC"
                                ? styles.badgeInfo
                                : styles.badgeSuccess
                            }`}
                            style={{ marginRight: '0.25rem' }}
                          >
                            {workflow.workflow_type}
                          </span>
                          <small style={{ color: '#6c757d' }}>
                            {workflow.workflow_name}
                          </small>
                          {index === 0 &&
                            result.matched_workflows.length > 1 && <br />}
                        </div>
                      ))}
                    {result.matched_workflows.length > 2 && (
                      <small style={{ color: '#6c757d' }}>
                        +{result.matched_workflows.length - 2} more workflow
                        {result.matched_workflows.length - 2 !== 1 ? "s" : ""}
                      </small>
                    )}
                  </div>
                </td>
                <td data-label="Actions">
                  <div className={styles.actionButtons}>
                    {result.matched_workflows.slice(0, 1).map((workflow) => (
                      <button
                        key={workflow.workflow_id}
                        onClick={() => {
                          // Track PagerDuty link click
                          trackLeadershipSearchPagerDutyClick({
                            streamId: result.delivery_stream.id,
                            streamName: result.delivery_stream.name,
                            workflowId: workflow.workflow_id,
                            workflowType: workflow.workflow_type
                          });

                          window.open(workflow.pagerduty_url, "_blank");
                        }}
                        title="Open in PagerDuty"
                        aria-label={`Open ${result.delivery_stream.name} in PagerDuty`}
                        className={`${styles.iconButton} ${styles.iconButtonPrimary}`}
                      >
                        <FaExternalLinkAlt />
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            ))
          ) : isSearchActive ? (
            <tr>
              <td colSpan="4" style={{ textAlign: 'center', color: '#6c757d', padding: '1.5rem 0.75rem' }}>
                No leadership responders found matching "{searchTerm}"
              </td>
            </tr>
          ) : (
            <tr>
              <td colSpan="4" style={{ textAlign: 'center', color: '#6c757d', padding: '1.5rem 0.75rem' }}>
                Enter a name above to search for leadership responders
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Results Cards - Mobile */}
      <div className={styles.cardList}>
        {searchResults && searchResults.length > 0 ? (
          searchResults.map((result) => (
            <div key={result.delivery_stream.id} className={styles.card}>
              <div className={styles.cardHeader}>
                {canViewDeliveryStreams ? (
                  <Link
                    to={`/configuration/deliverystream/${result.delivery_stream.id}`}
                    className={styles.cardTitle}
                    title={
                      hasConfigurationPrivilege
                        ? "Edit delivery stream"
                        : "View delivery stream (read-only)"
                    }
                  >
                    {result.delivery_stream.name}
                  </Link>
                ) : (
                  <span className={styles.cardTitle}>
                    {result.delivery_stream.name}
                  </span>
                )}
                {!hasConfigurationPrivilege && (
                  <span className={styles.badge}>Read Only</span>
                )}
              </div>
              <div className={styles.cardBody}>
                <div className={styles.cardField}>
                  <span className={styles.cardLabel}>Portfolio:</span>
                  <span className={styles.cardValue}>
                    {result.delivery_stream.portfolio}
                  </span>
                </div>
                <div className={styles.cardField}>
                  <span className={styles.cardLabel}>Workflows:</span>
                  <span className={styles.cardValue}>
                    {result.matched_workflows
                      .slice(0, 2)
                      .map((workflow) => (
                        <span key={workflow.workflow_id}>
                          <span
                            className={`${styles.badge} ${
                              workflow.workflow_type === "IC"
                                ? styles.badgeInfo
                                : styles.badgeSuccess
                            }`}
                          >
                            {workflow.workflow_type}
                          </span>{" "}
                          {workflow.workflow_name}
                        </span>
                      ))
                      .reduce((prev, curr, idx) => [
                        prev,
                        idx > 0 && <br key={idx} />,
                        curr,
                      ])}
                    {result.matched_workflows.length > 2 && (
                      <>
                        <br />
                        <small style={{ color: "var(--text-secondary)" }}>
                          +{result.matched_workflows.length - 2} more
                        </small>
                      </>
                    )}
                  </span>
                </div>
              </div>
              <div className={styles.cardActions}>
                {result.matched_workflows.slice(0, 1).map((workflow) => (
                  <button
                    key={workflow.workflow_id}
                    className={`${styles.cardButton} ${styles.cardButtonPrimary}`}
                    onClick={() => {
                      // Track PagerDuty link click
                      trackLeadershipSearchPagerDutyClick({
                        streamId: result.delivery_stream.id,
                        streamName: result.delivery_stream.name,
                        workflowId: workflow.workflow_id,
                        workflowType: workflow.workflow_type
                      });

                      window.open(workflow.pagerduty_url, "_blank");
                    }}
                    aria-label={`Open ${result.delivery_stream.name} in PagerDuty`}
                  >
                    <FaExternalLinkAlt /> PagerDuty
                  </button>
                ))}
              </div>
            </div>
          ))
        ) : isSearchActive ? (
          <div className={styles.emptyState}>
            No leadership responders found matching "{searchTerm}"
          </div>
        ) : (
          <div className={styles.emptyState}>
            Enter a name above to search for leadership responders
          </div>
        )}
      </div>

      {/* Results Summary */}
      {searchResults && searchResults.length > 0 && (
        <div className="mt-3">
          <small className="text-muted">
            Total: {searchResults.length} delivery stream
            {searchResults.length !== 1 ? "s" : ""} containing leadership
            responder "{searchTerm}"
          </small>
        </div>
      )}
    </div>
  );
}

export default LeadershipSearch;
